import type { ExactVelden, GrootboekGegevens, KostenplaatsGegevens } from '../../../gedeeld/types'
import { BvVinkjes, KeuzeVeld, naamVan, Rondjes, TekstVeld } from './ExactDelen'

/* ===========================================================================
 *  Grootboekrekeningen en kostenplaatsen op de Exact-kaart
 *
 *  Beide kunnen in meer bv's tegelijk (MEER_BVS). Per bv staat erbij wat de
 *  server zag: bestaat het rekeningnummer daar al, en onder welke naam. Een
 *  rekening "aanmaken" die al bestaat slaat de server daar over; een rekening
 *  "wijzigen" die er niet is, kan niet.
 * =========================================================================== */

const ACTIES: { key: GrootboekGegevens['actie']; label: string; uitleg: string; gevaar?: boolean }[] = [
  { key: 'aanmaken', label: 'Aanmaken', uitleg: 'Een nieuwe rekening in elke aangevinkte bv.' },
  { key: 'wijzigen', label: 'Wijzigen', uitleg: 'Een bestaande rekening aanpassen: omschrijving, soort, kant, type en btw.' },
  { key: 'blokkeren', label: 'Blokkeren', uitleg: 'Er kan daarna niet meer op geboekt worden. Wat er al op staat, blijft staan.', gevaar: true },
  { key: 'deblokkeren', label: 'Deblokkeren', uitleg: 'Er kan weer op geboekt worden.' },
]

/**
 * De soorten rekening in Exact (FinancialGLAccounts.Type), met de codes uit
 * de documentatie van Exact (HlpRestAPIResourcesDetails.aspx?name=
 * FinancialGLAccounts, nagekeken 19 oktober 2026). Zodra grootboek.ts in
 * de dashboard-repo een eigen lijst heeft, moeten deze twee gelijk blijven.
 */
export const GB_TYPES: { code: number; naam: string; groep: 'Balans' | 'Winst & verlies' | 'Overig' }[] = [
  { code: 10, naam: 'Kas', groep: 'Balans' },
  { code: 12, naam: 'Bank', groep: 'Balans' },
  { code: 14, naam: 'Creditcard', groep: 'Balans' },
  { code: 16, naam: 'Betaaldienst', groep: 'Balans' },
  { code: 20, naam: 'Debiteuren', groep: 'Balans' },
  { code: 21, naam: 'Vooruitbetalingen debiteuren', groep: 'Balans' },
  { code: 22, naam: 'Crediteuren', groep: 'Balans' },
  { code: 24, naam: 'Btw', groep: 'Balans' },
  { code: 25, naam: 'Te betalen aan werknemers', groep: 'Balans' },
  { code: 26, naam: 'Vooruitbetaalde kosten', groep: 'Balans' },
  { code: 27, naam: 'Nog te betalen kosten', groep: 'Balans' },
  { code: 29, naam: 'Te betalen vennootschapsbelasting', groep: 'Balans' },
  { code: 30, naam: 'Vaste activa', groep: 'Balans' },
  { code: 32, naam: 'Overige activa', groep: 'Balans' },
  { code: 35, naam: 'Cumulatieve afschrijvingen', groep: 'Balans' },
  { code: 40, naam: 'Voorraad', groep: 'Balans' },
  { code: 50, naam: 'Aandelenkapitaal', groep: 'Balans' },
  { code: 52, naam: 'Ingehouden winst', groep: 'Balans' },
  { code: 55, naam: 'Langlopende schulden', groep: 'Balans' },
  { code: 60, naam: 'Kortlopend deel van schulden', groep: 'Balans' },
  { code: 90, naam: 'Algemeen', groep: 'Balans' },
  { code: 100, naam: 'Te betalen belastingen', groep: 'Balans' },
  { code: 110, naam: 'Omzet', groep: 'Winst & verlies' },
  { code: 111, naam: 'Kostprijs van de omzet', groep: 'Winst & verlies' },
  { code: 120, naam: 'Overige kosten', groep: 'Winst & verlies' },
  { code: 121, naam: 'Verkoop- en algemene kosten', groep: 'Winst & verlies' },
  { code: 122, naam: 'Afschrijvingskosten', groep: 'Winst & verlies' },
  { code: 123, naam: 'Onderzoek en ontwikkeling', groep: 'Winst & verlies' },
  { code: 125, naam: 'Personeelskosten', groep: 'Winst & verlies' },
  { code: 126, naam: 'Werkgeverslasten', groep: 'Winst & verlies' },
  { code: 130, naam: 'Buitengewone kosten', groep: 'Winst & verlies' },
  { code: 140, naam: 'Buitengewone baten', groep: 'Winst & verlies' },
  { code: 150, naam: 'Vennootschapsbelasting', groep: 'Winst & verlies' },
  { code: 160, naam: 'Rentebaten', groep: 'Winst & verlies' },
  { code: 300, naam: 'Jaarafsluiting: reflectie', groep: 'Overig' },
  { code: 301, naam: 'Jaarafsluiting: indirecte kosten', groep: 'Overig' },
  { code: 302, naam: 'Jaarafsluiting: directe kosten', groep: 'Overig' },
]

export function typeNaam(code: number | null): string | null {
  if (code === null) return null
  return GB_TYPES.find((t) => t.code === code)?.naam ?? null
}

export function ExactGrootboek({ v, g, onV }: { v: ExactVelden; g: GrootboekGegevens; onV: (v: ExactVelden) => void }) {
  const zet = (n: Partial<GrootboekGegevens>) => onV({ ...v, gegevens: { ...g, ...n } })
  const alleenCode = g.actie === 'blokkeren' || g.actie === 'deblokkeren'
  const gekozenType = GB_TYPES.find((t) => t.code === g.type)
  const onbekendType = g.type !== null && !gekozenType
  /* Een omzettype op een balansrekening is bijna altijd een vergissing van
     de AI (of van ons); zeggen, niet tegenhouden: dat is aan de server. */
  const scheef = gekozenType && g.soort && gekozenType.groep !== 'Overig' && (gekozenType.groep === 'Balans') !== (g.soort === 'B')

  return (
    <>
      <Rondjes label="Wat" breed waarde={g.actie} onWaarde={(actie) => zet({ actie })} keuzes={ACTIES} />
      <TekstVeld label="Rekeningnummer" mono waarde={g.code} onWaarde={(code) => zet({ code: code.trim() })} placeholder="bijv. 4135" />
      {!alleenCode && <TekstVeld label="Omschrijving" waarde={g.omschrijving} onWaarde={(omschrijving) => zet({ omschrijving })} />}
      {alleenCode && <p className="breed stil ex-noot">Bij {g.actie} telt alleen het rekeningnummer; de rest van de rekening blijft zoals hij is.</p>}

      {!alleenCode && (
        <>
          <TekstVeld label="Zoals rekening" mono waarde={g.zoals} onWaarde={(zoals) => zet({ zoals })} placeholder="optioneel, bijv. 4130"
            naam={g.zoals?.trim() ? `Soort, kant, type en btw overnemen van ${g.zoals.trim()}${naamVan(v.berekend, 'rekening', g.zoals) ? ` (${naamVan(v.berekend, 'rekening', g.zoals)})` : ''}` : null} />
          <KeuzeVeld label="Standaard btw" waarde={g.btw} onWaarde={(btw) => zet({ btw })} keuzes={v.keuzes?.btw} leeg="Geen btw" />
          <Rondjes label="Soort" waarde={g.soort} onWaarde={(soort) => zet({ soort })} keuzes={[
            { key: 'W', kort: 'W', label: 'Winst & verlies', uitleg: 'Opbrengsten en kosten; gaat aan het eind van het jaar naar het resultaat.' },
            { key: 'B', kort: 'B', label: 'Balans', uitleg: 'Bezittingen en schulden; het saldo gaat mee naar het volgende jaar.' },
          ]} />
          <Rondjes label="Kant" waarde={g.kant} onWaarde={(kant) => zet({ kant })} keuzes={[
            { key: 'D', kort: 'D', label: 'Debet', uitleg: 'Normaal voor kosten en bezittingen.' },
            { key: 'C', kort: 'C', label: 'Credit', uitleg: 'Normaal voor opbrengsten en schulden.' },
          ]} />
          <label className="veld breed">
            <span className="veld-label">Type in Exact</span>
            <select className="invoer" value={g.type === null ? '' : String(g.type)} aria-invalid={onbekendType || undefined}
              onChange={(e) => zet({ type: e.target.value === '' ? null : Number(e.target.value) })}>
              <option value="">Kies…</option>
              {onbekendType && <option value={String(g.type)}>{g.type} (onbekend type)</option>}
              {(['Winst & verlies', 'Balans', 'Overig'] as const).map((groep) => (
                <optgroup key={groep} label={groep}>
                  {GB_TYPES.filter((t) => t.groep === groep).map((t) => <option key={t.code} value={String(t.code)}>{t.naam} ({t.code})</option>)}
                </optgroup>
              ))}
            </select>
            {scheef && <span className="ex-naam onbekend">Dit type hoort bij {gekozenType.groep.toLowerCase()}, maar de soort staat op {g.soort === 'B' ? 'balans' : 'winst & verlies'}.</span>}
          </label>
        </>
      )}

      <BvVinkjes mogelijk={v.mogelijk} gekozen={v.divisies} onGekozen={(divisies) => onV({ ...v, divisies })}
        perBv={v.berekend?.perBv} actie={g.actie} code={g.code} />
    </>
  )
}

const KP_ACTIES: { key: KostenplaatsGegevens['actie']; label: string; uitleg: string; gevaar?: boolean }[] = [
  { key: 'aanmaken', label: 'Aanmaken', uitleg: 'Een nieuwe code in elke aangevinkte bv.' },
  { key: 'wijzigen', label: 'Wijzigen', uitleg: 'De omschrijving van een bestaande code aanpassen.' },
  { key: 'blokkeren', label: 'Blokkeren', uitleg: 'Er kan daarna niet meer op geboekt worden.', gevaar: true },
  { key: 'deblokkeren', label: 'Deblokkeren', uitleg: 'Er kan weer op geboekt worden.' },
]

export function ExactKostenplaats({ v, g, onV }: { v: ExactVelden; g: KostenplaatsGegevens; onV: (v: ExactVelden) => void }) {
  const zet = (n: Partial<KostenplaatsGegevens>) => onV({ ...v, gegevens: { ...g, ...n } })
  return (
    <>
      <Rondjes label="Soort" breed waarde={g.soort} onWaarde={(soort) => zet({ soort })} keuzes={[
        { key: 'kostenplaats', label: 'Kostenplaats', uitleg: 'Waar de kosten gemaakt worden: een vestiging, een afdeling, een straat.' },
        { key: 'kostendrager', label: 'Kostendrager', uitleg: 'Waarvoor de kosten gemaakt worden: een product, een project, een klant.' },
      ]} />
      <Rondjes label="Wat" breed waarde={g.actie} onWaarde={(actie) => zet({ actie })} keuzes={KP_ACTIES} />
      <TekstVeld label="Code" mono waarde={g.code} onWaarde={(code) => zet({ code: code.trim() })} />
      <TekstVeld label="Omschrijving" waarde={g.omschrijving} onWaarde={(omschrijving) => zet({ omschrijving })} />
      <BvVinkjes mogelijk={v.mogelijk} gekozen={v.divisies} onGekozen={(divisies) => onV({ ...v, divisies })}
        perBv={v.berekend?.perBv} actie={g.actie} code={g.code} />
    </>
  )
}
